/**
 * MultipleEntitlementsView Component
 *
 * Shows all active purchase entitlements grouped by tier.
 * Users can have multiple entitlements of each tier from different purchases.
 *
 * Updated to work with EntitlementProgress from backend.
 */

import { memo } from 'react';
import type { EntitlementProgress } from '../../types/jackpot';
import { TicketProgressCard } from './TicketProgressCard';
import { useTranslation } from '../../hooks/useTranslation';

interface MultipleEntitlementsViewProps {
  entitlements: EntitlementProgress[];
  className?: string;
}

interface TierSectionProps {
  title: string;
  subtitle: string;
  entitlements: EntitlementProgress[];
}

/**
 * Section for a specific tier showing all entitlements
 */
const TierSection = memo(function TierSection({ title, subtitle, entitlements }: TierSectionProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="font-medium text-slate-200">{title}</h5>
        <span className="text-xs text-amber-400">{subtitle}</span>
      </div>
      <div className="space-y-2">
        {entitlements.map((ent, i) => (
          <TicketProgressCard key={ent.entitlement_id} entitlement={ent} index={i} />
        ))}
      </div>
    </div>
  );
});

export const MultipleEntitlementsView = memo(function MultipleEntitlementsView({
  entitlements,
  className = '',
}: MultipleEntitlementsViewProps) {
  const { t } = useTranslation();

  // Filter out free_bonus tier (shown separately)
  const paidEntitlements = entitlements.filter((e) => e.tier !== 'free_bonus');

  // Group entitlements by tier
  const byTier = {
    basic: paidEntitlements.filter((e) => e.tier === 'basic'),
    pro: paidEntitlements.filter((e) => e.tier === 'pro'),
    enterprise: paidEntitlements.filter((e) => e.tier === 'enterprise'),
  };

  // Check if there are any entitlements
  const hasEntitlements = paidEntitlements.length > 0;

  if (!hasEntitlements) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-slate-400">{t('jackpot.noEntitlements', 'No active entitlements')}</p>
        <p className="text-sm text-slate-500 mt-2">
          {t('jackpot.purchaseToGetTickets', 'Purchase token packages to earn jackpot tickets!')}
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Basic tier entitlements */}
      {byTier.basic.length > 0 && (
        <TierSection
          title={t('jackpot.basicPackages', 'Basic Packages')}
          subtitle={`${byTier.basic.length} ${t('jackpot.active', 'active')}`}
          entitlements={byTier.basic}
        />
      )}

      {/* Pro tier entitlements */}
      {byTier.pro.length > 0 && (
        <TierSection
          title={t('jackpot.proPackages', 'Pro Packages')}
          subtitle={`${byTier.pro.length} ${t('jackpot.active', 'active')}`}
          entitlements={byTier.pro}
        />
      )}

      {/* Enterprise tier entitlements */}
      {byTier.enterprise.length > 0 && (
        <TierSection
          title={t('jackpot.enterprisePackages', 'Enterprise Packages')}
          subtitle={`${byTier.enterprise.length} ${t('jackpot.active', 'active')}`}
          entitlements={byTier.enterprise}
        />
      )}
    </div>
  );
});
